'use es6';

import { Component } from 'react';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { InputStaticLabel } from 'foundations-components/transitional/components/InputStaticLabel';
import { Flex } from 'foundations-components/components/Flex';
import colr from './vendor/colr';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const modesMap = ['RGB', 'HSB', 'HSL'];
const ParamInputLabel = props => {
  return /*#__PURE__*/_jsx(InputStaticLabel, Object.assign({
    "data-test-id": "colorpicker-param-input-label",
    display: "block",
    labelWrapperStyles: {
      paddingInlineStart: '250',
      inlineSize: '22px'
    }
  }, props));
};
export default class Params extends Component {
  static getDerivedStateFromProps(props, state) {
    if (props.hex !== state.prevHex) {
      return {
        inputValue: props.hex.replace('#', ''),
        prevHex: props.hex
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.onHexHandler = evt => {
      const inputValue = evt.target.value.replace(/[^0-9a-f]/gi, '').slice(0, 6);
      this.setState({
        inputValue
      });
      if (inputValue.length < 6) return;
      try {
        const color = colr.fromHex(inputValue);
        this.props.onChange({
          hex: color.toHex()
        });
      } catch (e) {
        /* eslint no-empty:0 */
      }
    };
    this.onAlphaHandler = evt => {
      let alpha = parseInt(evt.target.value, 10);
      if (isNaN(alpha)) {
        alpha = 0;
      }
      alpha = Math.max(0, alpha);
      alpha = Math.min(alpha, 100);
      this.props.onChange({
        alpha
      });
    };
    this.onColorChannelChange = (index, evt) => {
      const value = this.getChannelInRange(evt.target.value, index);
      const colorChannel = this.getColorChannel();
      colorChannel[index] = value;
      const {
        hex,
        hue
      } = this.getColorByChannel(colorChannel);
      this.props.onChange({
        hex,
        hue
      });
    };
    this.getChannelInRange = (value, index) => {
      const channelMap = {
        RGB: [[0, 255], [0, 255], [0, 255]],
        HSB: [[0, 360], [0, 100], [0, 100]],
        HSL: [[0, 360], [0, 100], [0, 100]]
      };
      const mode = this.props.mode;
      const range = channelMap[mode][index];
      let result = parseInt(value, 10);
      if (isNaN(result)) {
        result = 0;
      }
      result = Math.max(range[0], result);
      result = Math.min(result, range[1]);
      return result;
    };
    this.getColorByChannel = colorChannel => {
      const {
        mode
      } = this.props;
      let hex;
      let hue;
      switch (mode) {
        case 'RGB':
          hex = colr.fromRgbArray(colorChannel).toHex();
          break;
        case 'HSB':
          hex = colr.fromHsvArray(colorChannel).toHex();
          hue = colorChannel[0];
          break;
        case 'HSL':
          hex = colr.fromHslArray(colorChannel).toHex();
          hue = colorChannel[0];
          break;
        default:
          hex = colr.fromRgbArray(colorChannel).toHex();
      }
      return {
        hex,
        hue
      };
    };
    this.getColorChannel = () => {
      const {
        hex,
        hue,
        mode
      } = this.props;
      const color = colr.fromHex(hex);
      let result;
      switch (mode) {
        case 'RGB':
          result = color.toRgbArray();
          break;
        case 'HSB':
          result = [hue, ...color.toHsvArray().slice(1)];
          break;
        case 'HSL':
          result = [hue, ...color.toHslArray().slice(1)];
          break;
        default:
          result = color.toRgbArray();
      }
      return result;
    };
    this.state = {
      inputValue: props.hex.replace('#', ''),
      prevHex: props.hex
    };
  }
  renderRgbaInput(colorChannelIndex) {
    const colorChannel = this.getColorChannel();
    const keyName = this.props.mode[colorChannelIndex];
    return /*#__PURE__*/_jsx(ParamInputLabel, {
      text: keyName,
      children: /*#__PURE__*/_jsx(NumberInput, {
        maxLength: "A",
        value: colorChannel[colorChannelIndex],
        onChange: this.onColorChannelChange.bind(null, colorChannelIndex)
      })
    }, keyName);
  }
  renderAlphaInput() {
    return /*#__PURE__*/_jsx(ParamInputLabel, {
      text: "A",
      children: /*#__PURE__*/_jsx(NumberInput, {
        value: this.props.alpha,
        onChange: this.onAlphaHandler
      })
    }, "A");
  }
  renderRgba() {
    const {
      hexOnly,
      includeAlpha
    } = this.props;
    let inputs = [];
    if (!hexOnly) {
      inputs = inputs.concat([this.renderRgbaInput(0), this.renderRgbaInput(1), this.renderRgbaInput(2)]);
    }
    if (includeAlpha) inputs.push(this.renderAlphaInput());
    return inputs;
  }
  render() {
    const {
      fieldsetSize
    } = this.props;
    return /*#__PURE__*/_jsx(Fieldset, {
      "data-test-id": "colorpicker-input-params",
      _size: fieldsetSize,
      marginInlineStart: "200",
      inlineSize: "115px",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        rowGap: "200",
        children: [this.renderRgba(), /*#__PURE__*/_jsx(ParamInputLabel, {
          text: "#",
          children: /*#__PURE__*/_jsx(TextInput, {
            maxLength: "7",
            onChange: this.onHexHandler,
            value: this.state.inputValue
          })
        })]
      })
    });
  }
}

/**
 * @typedef {Object} ParamsProps
 * @property {number} [alpha]
 * @property {'default'|'small'} [fieldsetSize]
 * @property {string} hex - required
 * @property {boolean} [hexOnly]
 * @property {number} hue - required
 * @property {boolean} [includeAlpha]
 * @property {'RGB'|'HSB'|'HSL'} [mode]
 * @property {function({hex?: string, hue?: number, alpha?: number}): void} [onChange]
 */
Params.defaultProps = {
  mode: modesMap[0]
};